import { z } from 'zod';
import { isoDateTime } from './common';
import { evenementAudienceSchema } from './evenement';
import {
  inscriptionProfileSchema,
  paymentPreferenceSchema,
  paymentSchema,
} from './inscription';

/**
 * Inscription d'un participant à un événement scientifique hébergé sur MTC
 * (`evenement.registrationMode === 'hosted-mtc'`).
 *
 * Volontairement séparé de `inscriptions/{id}` (les formations MTC) parce que
 * le lifecycle est différent : pas de groupes / cohortes / certificats MTC,
 * pas de mise à jour des `members.enrolledFormationIds` (un inscrit à un
 * congrès partenaire n'est pas un membre MTC formé), pas de cohort, et l'on
 * peut sélectionner plusieurs ateliers par inscription.
 */

export const evenementInscriptionStatusSchema = z.enum([
  'pending',    // créée, en attente de paiement / confirmation
  'confirmed',  // confirmée par l'admin (place réservée, paiement à venir)
  'paid',       // payée en totalité
  'cancelled',  // annulée — places (général + ateliers) libérées
]);
export type EvenementInscriptionStatus = z.infer<typeof evenementInscriptionStatusSchema>;

/**
 * Charge utile envoyée par le formulaire public à la création.
 * Reuse `inscriptionProfileSchema` pour faciliter le pré-remplissage depuis le
 * profil membre — mais en pratique seuls firstName/lastName/email/phone/cabinet
 * sont demandés par le formulaire event (le reste reste optionnel au schema).
 */
export const evenementInscriptionCreateSchema = z.object({
  evenementId: z.string().min(1),
  profile: inscriptionProfileSchema,
  /** Statut déclaré par le participant. Drive la disponibilité des packs
   *  audience-restricted + sert l'analytics admin. Sans pack restreint, reste
   *  utile pour la segmentation marketing — donc obligatoire à la saisie. */
  audience: evenementAudienceSchema.default('praticien'),
  /** Justificatif du statut déclaré (carte étudiant, diplôme, carte
   *  professionnelle). Image ou PDF ≤ 5 Mo, uploadé avant la soumission. */
  audienceProofUrl: z.string().url().nullable().default(null),
  /** Présence à la session plénière / accueil général. */
  generalIncluded: z.boolean().default(true),
  /** IDs des ateliers sélectionnés. Doit être un seul atelier par `timeSlotId`
   *  (la règle est validée côté formulaire ET côté Cloud Function). */
  atelierIds: z.array(z.string()).default([]),
  /** IDs des masterclasses sélectionnées. Même exclusivité par créneau
   *  partagée avec les ateliers (un créneau = un atelier OU une masterclass). */
  masterclassIds: z.array(z.string()).default([]),
  /** ID du pack sélectionné (optionnel). Lorsqu'un pack est choisi, son
   *  `price` remplace la somme des items inclus dans le pack ; les items
   *  ajoutés au-delà du pack sont facturés en supplément à leur prix nominal. */
  packId: z.string().nullable().default(null),
  /** Somme calculée côté client. Recomputable côté Cloud Function pour
   *  défense en profondeur contre une altération client. */
  totalAmount: z.number().nonnegative().default(0),
  paymentPreference: paymentPreferenceSchema,
});
export type EvenementInscriptionCreate = z.infer<typeof evenementInscriptionCreateSchema>;

/** Forme complète du doc Firestore. */
export const evenementInscriptionSchema = evenementInscriptionCreateSchema.extend({
  id: z.string(),
  status: evenementInscriptionStatusSchema,
  payments: z.array(paymentSchema).default([]),
  /** UID Firebase Auth du membre — défini à la création quand le visiteur est
   *  connecté + email vérifié, ou rétroactivement par `onMemberWrite` quand
   *  un membre vérifie un email matchant `profile.email`. */
  memberUid: z.string().nullable().default(null),
  adminNotes: z.string().max(2000).optional(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type EvenementInscription = z.infer<typeof evenementInscriptionSchema>;
